// src/modules/app-views/landingPages/pages/Document/components/WebsitePreviewPanel.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Button, Flex, Segmented, Tooltip, Typography, Space } from 'antd';
import {
  DesktopOutlined,
  MobileOutlined,
  ReloadOutlined,
  CloudUploadOutlined,
  ExportOutlined,
  GlobalOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import landingPagesService from '../../../service';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const HELLO_WORLD_HTML = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Preview</title>
  <style>
    body {
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .card {
      background: #fff;
      border-radius: 16px;
      padding: 48px 40px;
      text-align: center;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      max-width: 480px;
      width: 90%;
    }
    h1 { color: #1a1a2e; font-size: 2rem; margin: 0 0 12px; }
    p { color: #666; font-size: 1rem; margin: 0; }
  </style>
</head>
<body>
  <div class="card">
    <h1>Hello World!</h1>
    <p>Use o assistente de IA para gerar sua landing page.</p>
  </div>
</body>
</html>`;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WebsitePreviewPanel = ({ documentId, htmlContent, externalUrl }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [device, setDevice] = useState('desktop');
  const [iframeKey, setIframeKey] = useState(0);
  const [deploying, setDeploying] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    setIframeKey(prev => prev + 1);
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    if (externalUrl) window.open(externalUrl, '_blank', 'noopener,noreferrer');
  }, [externalUrl]);

  const handleDeploy = async () => {
    if (!documentId || documentId === 'add') return;
    setDeploying(true);
    try {
      await landingPagesService.deployToNetlify(documentId);
    } finally {
      setDeploying(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const iframeWidth = device === 'mobile' ? 375 : '100%';
  const iframeSrcDoc = htmlContent || HELLO_WORLD_HTML;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // External URL mode — most production sites block iframe embedding.
  // Show a clear link card instead of a broken/blank iframe.
  if (externalUrl) {
    return (
      <Card
        title={translateX('website_preview')}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
      >
        <Flex
          vertical
          align="center"
          justify="center"
          gap={24}
          style={{ flex: 1 }}
        >
          {/* URL display */}
          <GlobalOutlined style={{ fontSize: 48, opacity: 0.3 }} />

          <Flex vertical align="center" gap={8}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {translateX('external_page_url')}
            </Typography.Text>
            <Typography.Text
              copyable
              ellipsis
              style={{ maxWidth: 480, fontSize: 14, fontWeight: 500 }}
            >
              {externalUrl}
            </Typography.Text>
          </Flex>

          <Button
            type="primary"
            size="large"
            icon={<ExportOutlined />}
            onClick={handleOpenInNewTab}
          >
            {translateX('open_in_new_tab')}
          </Button>

          {/* Explanation */}
          <Flex
            gap={8}
            align="flex-start"
            style={{
              maxWidth: 480,
              padding: '12px 16px',
              backgroundColor: 'rgba(0,0,0,0.03)',
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <WarningOutlined style={{ color: '#faad14', marginTop: 2, flexShrink: 0 }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {translateX('iframe_external_not_supported')}
            </Typography.Text>
          </Flex>
        </Flex>
      </Card>
    );
  }

  // AI-generated / create mode — render HTML in sandboxed iframe
  return (
    <Card
      title={translateX('website_preview')}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        },
      }}
      extra={
        <Flex gap={8} align="center">
          <Segmented
            size="small"
            value={device}
            onChange={setDevice}
            options={[
              { value: 'desktop', icon: <DesktopOutlined /> },
              { value: 'mobile', icon: <MobileOutlined /> },
            ]}
          />

          <Tooltip title={translateX('refresh')}>
            <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} />
          </Tooltip>

          <Tooltip title={translateX('deploy_netlify')}>
            <Button
              size="small"
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={handleDeploy}
              loading={deploying}
              disabled={!htmlContent || !documentId || documentId === 'add'}
            >
              {translateX('deploy_netlify')}
            </Button>
          </Tooltip>
        </Flex>
      }
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          padding: device === 'mobile' ? 16 : 0,
          overflow: 'auto',
        }}
      >
        <iframe
          key={iframeKey}
          srcDoc={iframeSrcDoc}
          title="Website Preview"
          style={{
            width: iframeWidth,
            height: '100%',
            border: device === 'mobile' ? '1px solid #d9d9d9' : 'none',
            borderRadius: device === 'mobile' ? 8 : 0,
            backgroundColor: '#fff',
            minHeight: 400,
          }}
          // srcDoc is same-origin as parent — omit allow-same-origin to prevent
          // the sandboxed content from accessing parent cookies/localStorage.
          sandbox="allow-scripts"
        />
      </div>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WebsitePreviewPanel;
